import { useEffect, useState } from 'react'
import { ServiceDTO } from '../interfaces/Services/Services'
import { DeliveryEnum, PaymentModeId } from '../interfaces'
// API Django (REACT_APP_URL_PATH_API_DJANGO): /api/service/by-payment-mode,
// /api/service/by-delivery-type y /api/service/locked-by-application. Son drop-in de los
// equivalentes .NET (misma firma y retorno), así que la lógica del hook no cambia.
import {
  getServicesFromPaymentModeDjango,
  getServicesFromDeliveryTypeDjango,
  getAppLockedServicesDjango,
} from '../services/BusinessUnitServices'
import { useAppSelector } from '../store/hooks'

export function useAllowedServices(paymentMode?: PaymentModeId) {
  const applicationID = useAppSelector((s) => s.inmutable.appData.applicationID)
  const effectiveMode = paymentMode ?? PaymentModeId.CREDIT

  const [paymentModeServices, setPaymentModeServices] = useState<ServiceDTO[]>([])
  const [deliveryTypeServices, setDeliveryTypeServices] = useState<ServiceDTO[]>([])
  const [appBlockedServices, setAppBlockedServices] = useState<ServiceDTO[]>([])
  const [allowedServices, setAllowedServices] = useState<ServiceDTO[]>([])
  const [loadedFlags, setLoadedFlags] = useState({ payment: false, delivery: false, blocked: false })

  useEffect(() => {
    const load = async () => {
      const res = await getServicesFromPaymentModeDjango(effectiveMode)
      if (!res.didError && Array.isArray(res.model)) setPaymentModeServices(res.model)
      setLoadedFlags((f) => ({ ...f, payment: true }))
    }
    load()
  }, [effectiveMode])

  useEffect(() => {
    const load = async () => {
      // Sin is_international: devuelve nacionales + internacionales, igual que el .NET
      // (que no tenía ese filtro). Aquí solo se usa como intersección sobre los servicios
      // de la modalidad de pago, así que no puede "abrir" la lista de más.
      const res = await getServicesFromDeliveryTypeDjango(DeliveryEnum.OFFICE)
      if (!res.didError && Array.isArray(res.model)) setDeliveryTypeServices(res.model)
      setLoadedFlags((f) => ({ ...f, delivery: true }))
    }
    load()
  }, [])

  useEffect(() => {
    if (applicationID == null) return
    let cancelled = false
    const load = async () => {
      // El block-list es la barrera de seguridad del cliente: un fallo transitorio NO
      // debe "abrir" la lista en silencio (dejar seleccionable un servicio que debería
      // estar bloqueado). Reintentamos una vez; si aún falla, lo avisamos por consola y
      // seguimos —el backend es la barrera autoritativa y rechaza igual un servicio
      // bloqueado—. Fail-open deliberado tras reintento, no por error enmascarado.
      let res = await getAppLockedServicesDjango(applicationID)
      if (res.didError) res = await getAppLockedServicesDjango(applicationID)
      if (cancelled) return
      if (!res.didError && Array.isArray(res.model)) {
        setAppBlockedServices(res.model)
      } else {
        console.warn(
          '[useAllowedServices] no se pudo cargar la lista de servicios bloqueados; ' +
            'el filtro de bloqueados queda delegado al backend.'
        )
      }
      setLoadedFlags((f) => ({ ...f, blocked: true }))
    }
    load()
    return () => {
      cancelled = true
    }
  }, [applicationID])

  useEffect(() => {
    if (!loadedFlags.payment || !loadedFlags.delivery || !loadedFlags.blocked) return
    let base = paymentModeServices
    if (deliveryTypeServices.length > 0) {
      const deliverySet = new Set(deliveryTypeServices.map((s) => s.serviceID))
      base = base.filter((s) => deliverySet.has(s.serviceID))
    }
    if (appBlockedServices.length > 0) {
      const blocked = new Set(appBlockedServices.map((s) => s.serviceID))
      base = base.filter((s) => !blocked.has(s.serviceID))
    }
    setAllowedServices(base)
  }, [paymentModeServices, deliveryTypeServices, appBlockedServices, loadedFlags])

  return { allowedServices }
}
